import * as React from 'react';
import styled from 'styled-components';
import CardTopSection, {CardTopSectionProps} from './CardTopSection';
import CardMiddleSection, {CardMiddleSectionProps} from './CardMiddleSection';
import CardBottomSection, {CardBottomSectionProps} from './CardBottomSection';
import {BOXSHADOWS, COLORS, defaultOnMouseDownHandler, focusStyles, useActionWithin} from '../../index';

export const CardTopLevelContainerStyles = styled.div`
  background-color: ${COLORS.white};
  min-width: 240px;
  overflow: hidden;

  border-radius: 8px;

  background-clip: padding-box;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;

  .elevated & {
    box-shadow: ${BOXSHADOWS.BOXSHADOW_L1};
  }

  .outline & {
    border: 1px solid ${COLORS.neutral_200};
  }
`;


export const CardContainerStyles = styled.div`
  &.interactive:focus {
    ${focusStyles}
  }

  &.interactive:not(.disabled) {
    cursor: pointer;

    &.outline:not(.action-within) {
      ${CardTopLevelContainerStyles} {
        &:hover {
          background-color: ${COLORS.primary_20};
        }

        &:active, &.active-state {
          background-color: ${COLORS.primary_100};
        }
      }
    }

    &.elevated:not(.action-within) {
      ${CardTopLevelContainerStyles} {
        &:hover {
          box-shadow: ${BOXSHADOWS.BOXSHADOW_L3};
        }

        &:active, &.active-state {
          box-shadow: ${BOXSHADOWS.BOXSHADOW_L2};
        }
      }
    }
  }

  &.disabled {
    cursor: not-allowed;
    ${CardTopLevelContainerStyles}{
      border: 1px solid ${COLORS.neutral_100};
    }
  }
`;


export interface CardProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'tabIndex' | 'onMouseDown' | 'onKeyDown' | 'onClick'> {
  onCardClicked?: () => {};
  disabled?: boolean;
  maxWidth?: number | string;
  topSectionProps?: CardTopSectionProps;
  middleSectionProps?: CardMiddleSectionProps;
  bottomSectionProps?: CardBottomSectionProps;
  variant?: 'outline' | 'elevated';
}

const Card: React.FunctionComponent<CardProps> = ({
                                                    onCardClicked,
                                                    topSectionProps,
                                                    middleSectionProps,
                                                    bottomSectionProps,
                                                    disabled,
                                                    variant = 'elevated',
                                                    className,
                                                    maxWidth = 560,
                                                    ...rest
                                                  }: CardProps) => {


  const [actionsRefs, setActionsRefs] = React.useState<React.RefObject<HTMLElement>[]>([]);
  const checkBoxRef =  React.useRef<HTMLDivElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);

  useActionWithin(containerRef, [...actionsRefs, checkBoxRef]);

  const cls = `${!!onCardClicked ? 'interactive' : ''} ${disabled ? 'disabled' : ''} ${variant} ${className || ''}`;

  return (
    <CardContainerStyles ref={containerRef}
             className={cls}
             tabIndex={!!onCardClicked && !disabled ? 0 : -1}
             onMouseDown={defaultOnMouseDownHandler}
             onKeyDown={a => a.key === 'Enter' && !disabled && onCardClicked && onCardClicked()}
             onClick={e => !disabled && onCardClicked && onCardClicked()}
             data-testid={'card-wrapper'}
             {...rest}>
      <CardTopLevelContainerStyles style={{maxWidth: maxWidth}}>
        {
          topSectionProps &&
          <CardTopSection ref={checkBoxRef}
            {...topSectionProps} disabled={disabled}/>
        }
        {
          middleSectionProps &&
          <CardMiddleSection {...middleSectionProps} disabled={disabled}/>
        }
        {
          bottomSectionProps &&
          <CardBottomSection ref={instance => setActionsRefs(instance ?? [])}
                             {...bottomSectionProps} disabled={disabled}/>
        }
      </CardTopLevelContainerStyles>
    </CardContainerStyles>

  );
};

export default Card;
